/**
 * O que faz: mostra o tempo até fechar (dias do primeiro contato à primeira venda): mediana (P50),
 *            P75, P90, distribuição por faixas de dias e comparação por campanha, conjunto ou anúncio.
 *            Modo "faixa": resumo compacto que expande (topo de /vendas). Modo "completo": tudo aberto (dashboard).
 * Onde usa: ConteudoPainelVendas (/vendas) e SecaoTempoFechamentoDashboard (dashboard).
 * Conexões: useJornadaLeadsTempoFechamento e cálculo em src/lib/vendas/tempoFechamento.ts.
 *           Recebe só lead + data da venda e uma função que descreve a origem — sem dados pessoais.
 * Tabelas: leads e metricas_conversas via hook (somente SELECT com RLS).
 * Data: 05/10/2026.
 */
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronDown, Hourglass } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { DimensaoDesempenhoVenda, OrigemDescritaVenda } from '@/lib/vendas/resumoFiltroVendas';
import { useJornadaLeadsTempoFechamento } from '@/hooks/useTempoFechamentoVendas';
import {
  ID_BLOCO_TEMPO_FECHAMENTO,
  MINIMO_VENDAS_MEDIANA,
  MINIMO_VENDAS_PERCENTIS_ALTOS,
  agruparTempoFechamento,
  formatarDias,
  montarAmostrasTempoFechamento,
  posicaoNasFaixas,
  resumirTempoFechamento,
  type ResumoTempoFechamento,
  type VendaParaTempoFechamento,
} from '@/lib/vendas/tempoFechamento';

interface BlocoTempoFechamentoVendasProps<T extends VendaParaTempoFechamento> {
  // Todas as vendas do período: a busca da jornada roda uma vez e não a cada filtro.
  vendas: T[];
  // Recorte exibido (em /vendas, a lista filtrada; no dashboard, as mesmas vendas).
  vendasFiltradas: T[];
  clinicaId?: string | null;
  inicioPeriodo: Date | null;
  origemDe: (venda: T) => OrigemDescritaVenda;
  carregandoOrigens: boolean;
  modo: 'faixa' | 'completo';
  descricao: string;
  // Estado de quem carrega as vendas (o dashboard busca as próprias); evita mostrar "0" antes da hora.
  carregandoVendas?: boolean;
  erroVendas?: boolean;
  tentarNovamenteVendas?: () => void;
  // Ação extra no cabeçalho (ex.: "Ver em Vendas" no dashboard).
  acaoCabecalho?: ReactNode;
}

const SOMBRA_CARD = 'shadow-[0_1px_2px_rgba(19,41,27,0.05),0_3px_10px_rgba(19,41,27,0.05)]';
const DIMENSOES: Array<{ valor: DimensaoDesempenhoVenda; rotulo: string; semOrigem: string }> = [
  { valor: 'campanha', rotulo: 'Campanha', semOrigem: 'Sem campanha identificada' },
  { valor: 'conjunto', rotulo: 'Conjunto', semOrigem: 'Sem conjunto identificado' },
  { valor: 'anuncio', rotulo: 'Anúncio', semOrigem: 'Sem anúncio identificado' },
];

export function BlocoTempoFechamentoVendas<T extends VendaParaTempoFechamento>({
  vendas, vendasFiltradas, clinicaId, inicioPeriodo, origemDe, carregandoOrigens, modo, descricao,
  carregandoVendas = false, erroVendas = false, tentarNovamenteVendas, acaoCabecalho,
}: BlocoTempoFechamentoVendasProps<T>) {
  const location = useLocation();
  const vindoDoLink = location.hash === `#${ID_BLOCO_TEMPO_FECHAMENTO}`;
  // Na faixa, começa fechado para não empurrar a lista; quem chega pelo link do dashboard já vê aberto.
  const [expandido, setExpandido] = useState(modo === 'completo' || vindoDoLink);
  const [dimensao, setDimensao] = useState<DimensaoDesempenhoVenda>('campanha');
  const secaoRef = useRef<HTMLElement>(null);
  const jaRolou = useRef(false);
  const idConteudo = useId();

  const idsLeads = useMemo(() => vendas.map((venda) => venda.leadId), [vendas]);
  const jornada = useJornadaLeadsTempoFechamento(clinicaId, idsLeads, inicioPeriodo);

  // Guarda o cálculo: só refaz quando o recorte ou as datas da jornada mudam (não no hover).
  const { amostras, exclusoes } = useMemo(
    () => montarAmostrasTempoFechamento(vendasFiltradas, jornada.data ?? new Map()),
    [vendasFiltradas, jornada.data],
  );
  const resumo = useMemo(() => resumirTempoFechamento(amostras), [amostras]);
  const configuracao = DIMENSOES.find((item) => item.valor === dimensao)!;
  const grupos = useMemo(() => agruparTempoFechamento(amostras, (venda: T) => {
    const origem = origemDe(venda);
    if (dimensao === 'anuncio') return { chave: origem.anuncio ?? null, rotulo: origem.apelidoAnuncio ?? null };
    return { chave: (dimensao === 'campanha' ? origem.campanha : origem.conjunto) ?? null, rotulo: null };
  }), [amostras, dimensao, origemDe]);

  const carregando = carregandoVendas || (jornada.isLoading && idsLeads.length > 0);
  const erro = erroVendas || jornada.isError;
  const tentarNovamente = () => { if (erroVendas) tentarNovamenteVendas?.(); else void jornada.refetch(); };
  const temDados = !carregando && !erro && resumo.quantidade >= MINIMO_VENDAS_MEDIANA;

  useEffect(() => {
    // Vindo do link do dashboard (#tempo-ate-fechar), rola até o bloco uma única vez, depois que os
    // dados chegaram, para a página não "pular" de novo ao terminar de carregar.
    if (jaRolou.current || carregando || !vindoDoLink) return;
    jaRolou.current = true;
    const reduzirMovimento = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    secaoRef.current?.scrollIntoView?.({ behavior: reduzirMovimento ? 'auto' : 'smooth', block: 'start' });
  }, [carregando, vindoDoLink]);

  const percentis = [
    { chave: 'p50', titulo: 'Metade fecha em até', curto: 'Metade em até', valor: resumo.p50, rotulo: 'P50 · mediana', minimo: MINIMO_VENDAS_MEDIANA },
    { chave: 'p75', titulo: '3 em cada 4 em até', curto: '3 em 4 em até', valor: resumo.p75, rotulo: 'P75', minimo: MINIMO_VENDAS_PERCENTIS_ALTOS },
    { chave: 'p90', titulo: '9 em cada 10 em até', curto: '9 em 10 em até', valor: resumo.p90, rotulo: 'P90 · limite do follow-up', minimo: MINIMO_VENDAS_PERCENTIS_ALTOS },
  ] as const;

  const seloSemana = resumo.percentualAteSeteDias !== null && <span className="inline-flex shrink-0 items-center rounded-full bg-accent px-3 py-1 text-xs font-semibold tabular-nums text-accent-foreground">
    {Math.round(resumo.percentualAteSeteDias * 100)}% fecham na primeira semana
  </span>;

  // Mensagem única para falha, carregamento e poucos dados: nunca "0 dias" que pareça resultado real.
  const estadoSemNumeros = erro ? <div role="alert" className="rounded-xl border border-destructive/30 p-4 text-sm">
    <p>Não foi possível calcular o tempo até fechar.</p>
    <Button variant="outline" size="sm" className="mt-3 min-h-10 rounded-full" onClick={tentarNovamente}>Tentar novamente</Button>
  </div> : carregando ? <div role="status" className={modo === 'faixa' ? 'h-6 w-72 max-w-full rounded-md bg-muted animate-pulse motion-reduce:animate-none' : 'space-y-3'}>
    <span className="sr-only">Calculando o tempo até fechar…</span>
    {modo === 'completo' && <>
      <div className="grid grid-cols-3 gap-3">{[0, 1, 2].map((item) => <div key={item} className="h-20 rounded-xl bg-muted animate-pulse motion-reduce:animate-none" />)}</div>
      <div className="h-40 rounded-xl bg-muted animate-pulse motion-reduce:animate-none" />
    </>}
  </div> : <p className={cn('text-sm text-muted-foreground', modo === 'completo' && 'rounded-xl bg-muted p-4')}>
    {resumo.quantidade === 0
      ? 'Nenhuma primeira venda do CRM com data de primeiro contato neste recorte.'
      : `Só ${resumo.quantidade} ${resumo.quantidade === 1 ? 'venda entra' : 'vendas entram'} na conta. É preciso ter pelo menos ${MINIMO_VENDAS_MEDIANA} para calcular um tempo confiável; tente um período maior.`}
  </p>;

  return <section ref={secaoRef} id={ID_BLOCO_TEMPO_FECHAMENTO} aria-labelledby="titulo-tempo-fechamento" className={cn('scroll-mt-4 rounded-2xl border border-border bg-card p-4 sm:p-5', SOMBRA_CARD)}>
    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex min-w-0 items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent"><Hourglass aria-hidden="true" className="h-4 w-4 text-accent-foreground" /></span>
        <div className="min-w-0">
          <h2 id="titulo-tempo-fechamento" className="text-base font-semibold">Tempo até fechar</h2>
          {/* Na faixa, os três números ficam na própria linha do título: a resposta aparece sem abrir nada. */}
          {modo === 'faixa' && temDados
            ? <dl aria-label="Percentis do tempo até fechar" className="mt-1 flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm">
              {percentis.map((item) => <div key={item.chave} className="flex items-baseline gap-1.5">
                <dt className="text-muted-foreground">{item.curto}</dt>
                <dd className="font-semibold tabular-nums text-foreground">{item.valor === null ? '—' : formatarDias(item.valor)}</dd>
              </div>)}
            </dl>
            : <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{descricao}</p>}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 lg:shrink-0">
        {temDados && seloSemana}
        {acaoCabecalho}
        {modo === 'faixa' && temDados && <Button type="button" variant="ghost" size="sm" aria-expanded={expandido} aria-controls={idConteudo}
          onClick={() => setExpandido((atual) => !atual)} className="min-h-10 rounded-full px-3 font-semibold">
          {expandido ? 'Ocultar distribuição' : 'Ver distribuição'}
          {/* Só rotação (transform), sem animar dimensões; some com movimento reduzido. */}
          <ChevronDown aria-hidden="true" className={cn('h-4 w-4 transition-transform duration-200 motion-reduce:transition-none', expandido && 'rotate-180')} />
        </Button>}
      </div>
    </div>

    {!temDados && (modo === 'completo' || !carregando) && <div className="mt-4">{estadoSemNumeros}</div>}
    {modo === 'faixa' && carregando && <div className="mt-3">{estadoSemNumeros}</div>}

    {temDados && expandido && <div id={idConteudo} className="mt-4 animate-in fade-in-0 slide-in-from-top-1 duration-200 motion-reduce:animate-none">
      <ConteudoTempoFechamento modo={modo} resumo={resumo} percentis={percentis} grupos={grupos} configuracao={configuracao}
        dimensao={dimensao} aoTrocarDimensao={setDimensao} carregandoOrigens={carregandoOrigens} />
      <p className="mt-4 text-[11px] leading-relaxed text-muted-foreground">
        Primeiro contato = a primeira mensagem ou a entrada no CRM, o que vier antes (lead importado usa só a mensagem). Conta só a primeira venda de cada lead; consultas pagas ficam de fora.
        {exclusoes.recompra + exclusoes.semInicio + exclusoes.dataInconsistente > 0 && ` Fora da conta: ${[
          exclusoes.recompra ? `${exclusoes.recompra} ${exclusoes.recompra === 1 ? 'recompra' : 'recompras'}` : '',
          exclusoes.semInicio ? `${exclusoes.semInicio} sem data de primeiro contato` : '',
          exclusoes.dataInconsistente ? `${exclusoes.dataInconsistente} com venda anterior ao contato` : '',
        ].filter(Boolean).join(', ')}.`}
      </p>
    </div>}
  </section>;
}

interface ConteudoTempoFechamentoProps {
  modo: 'faixa' | 'completo';
  resumo: ResumoTempoFechamento;
  percentis: ReadonlyArray<{ chave: string; titulo: string; valor: number | null; rotulo: string; minimo: number }>;
  grupos: ReturnType<typeof agruparTempoFechamento>;
  configuracao: (typeof DIMENSOES)[number];
  dimensao: DimensaoDesempenhoVenda;
  aoTrocarDimensao: (dimensao: DimensaoDesempenhoVenda) => void;
  carregandoOrigens: boolean;
}

// Corpo do bloco: cartões dos percentis (só no modo completo; na faixa eles já estão no título),
// histograma por faixas e comparação por origem.
function ConteudoTempoFechamento({ modo, resumo, percentis, grupos, configuracao, dimensao, aoTrocarDimensao, carregandoOrigens }: ConteudoTempoFechamentoProps) {
  const maiorFaixa = resumo.faixas.reduce((maior, faixa) => Math.max(maior, faixa.quantidade), 0);
  const marcadores = posicionarMarcadores(percentis.flatMap((item) => item.valor === null ? [] : [{ chave: item.chave, rotulo: item.chave.toUpperCase(), dias: item.valor }]));
  // Escala comum das barras por origem: todas comparáveis entre si; mínimo de 7 dias evita barras gigantes com números pequenos.
  const escalaOrigens = Math.max(7, ...grupos.visiveis.map((grupo) => grupo.resumo.p90 ?? grupo.resumo.p50 ?? 0));

  return <div className="grid min-w-0 gap-5 xl:grid-cols-[minmax(0,1fr)_24rem]">
    <div className="min-w-0">
      {modo === 'completo' && <dl aria-label="Percentis do tempo até fechar" className="mb-5 grid grid-cols-3 gap-2 sm:gap-3">
        {percentis.map((item) => <div key={item.chave} className="min-w-0 rounded-xl bg-muted/70 p-3 sm:p-4">
          <dt className="text-[11px] font-medium leading-snug text-muted-foreground sm:text-xs">{item.titulo}</dt>
          <dd className="lg-display mt-1 text-lg font-bold tracking-tight tabular-nums text-foreground sm:text-2xl">{item.valor === null ? '—' : formatarDias(item.valor)}</dd>
          <dd className="mt-1 text-[11px] leading-snug text-muted-foreground">{item.valor === null ? `precisa de ${item.minimo} vendas` : item.rotulo}</dd>
        </div>)}
      </dl>}

      {/* Histograma: barras estáticas por render (sem animar altura) e marcadores dos percentis por cima. */}
      <figure>
        <div className="relative pt-9" role="img" aria-label={`Distribuição: ${resumo.faixas.map((faixa) => `${faixa.rotulo}, ${Math.round(faixa.percentual * 100)}%`).join('; ')}.`}>
          <div className="flex h-40 items-end gap-1.5 border-b border-border sm:gap-2">
            {resumo.faixas.map((faixa) => <div key={faixa.rotulo} className="flex h-full min-w-0 flex-1 flex-col items-center justify-end" title={`${faixa.rotulo}: ${faixa.quantidade} ${faixa.quantidade === 1 ? 'venda' : 'vendas'}`}>
              {/* Fundo do cartão + z-10: o rótulo fica legível quando um marcador tracejado passa por trás. */}
              <span className="relative z-10 mb-1 rounded bg-card px-0.5 text-[11px] tabular-nums text-muted-foreground">{Math.round(faixa.percentual * 100)}%</span>
              <div className="w-full max-w-14 rounded-t-md bg-[#8fb83a]" style={{ height: `${maiorFaixa > 0 ? Math.max(faixa.quantidade ? 4 : 0, (faixa.quantidade / maiorFaixa) * 100) * 0.8 : 0}%` }} />
            </div>)}
          </div>
          {marcadores.map((marcador) => <div key={marcador.chave} aria-hidden="true" className="pointer-events-none absolute bottom-0 top-0 -translate-x-1/2" style={{ left: `${marcador.posicao * 100}%` }}>
            <span className={cn('absolute left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-1.5 py-0.5 text-[11px] font-semibold tabular-nums',
              marcador.chave === 'p50' ? 'bg-foreground text-background' : 'bg-secondary text-foreground', marcador.linha === 0 ? 'top-0' : 'top-4')}>{marcador.rotulo}</span>
            <span className={cn('absolute bottom-0 left-1/2 border-l-2 border-dashed', marcador.chave === 'p50' ? 'border-foreground' : 'border-muted-foreground/60', marcador.linha === 0 ? 'top-5' : 'top-9')} />
          </div>)}
        </div>
        <div className="mt-1.5 flex gap-1.5 sm:gap-2" aria-hidden="true">
          {/* Eixo só com números ("1–3"): com " dias" o rótulo quebrava sozinho no celular. A unidade fica na legenda. */}
          {resumo.faixas.map((faixa) => <span key={faixa.rotulo} className="min-w-0 flex-1 text-center text-[11px] leading-tight tabular-nums text-muted-foreground">{faixa.rotulo.replace(' dias', '')}</span>)}
        </div>
        <figcaption className="mt-2 text-xs text-muted-foreground">Faixas em dias corridos · base: {resumo.quantidade} {resumo.quantidade === 1 ? 'venda' : 'vendas'} (uma por lead).</figcaption>
      </figure>
    </div>

    <div className="min-w-0">
      <div className="flex flex-col gap-3">
        <div>
          <h3 className="text-sm font-semibold">Por origem</h3>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">Ponto = mediana · barra = até onde fecham 9 em cada 10.</p>
        </div>
        {/* Abas segmentadas (seção 9.5 do design system), iguais às do ranking de Vendas. */}
        <div role="group" aria-label="Comparar tempo por" className="grid grid-cols-3 rounded-full bg-secondary p-1">
          {DIMENSOES.map((item) => <button key={item.valor} type="button" aria-pressed={dimensao === item.valor} onClick={() => aoTrocarDimensao(item.valor)}
            className={cn('min-h-9 rounded-full px-2 text-[13px] font-semibold transition-[background-color,color,box-shadow] duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none',
              dimensao === item.valor ? 'bg-card text-foreground shadow-[0_1px_2px_rgba(19,41,27,0.08),0_3px_8px_rgba(19,41,27,0.06)]' : 'text-muted-foreground hover:text-foreground')}>{item.rotulo}</button>)}
        </div>
      </div>
      {carregandoOrigens && dimensao !== 'anuncio'
        ? <p role="status" className="mt-4 rounded-xl bg-muted p-4 text-sm text-muted-foreground">Carregando campanhas e conjuntos dos leads…</p>
        : grupos.visiveis.length === 0
          ? <p className="mt-4 rounded-xl bg-muted p-4 text-sm text-muted-foreground">Nenhuma origem com {MINIMO_VENDAS_MEDIANA} ou mais vendas neste recorte.</p>
          // A key troca com a aba para a lista entrar com fade (só opacidade/transform).
          : <ol key={dimensao} className="mt-4 space-y-3.5 animate-in fade-in-0 slide-in-from-bottom-1 duration-200 motion-reduce:animate-none">
            {grupos.visiveis.slice(0, 6).map((grupo) => {
              const { p50, p90, quantidade } = grupo.resumo;
              const nome = grupo.rotulo || grupo.chave;
              return <li key={grupo.chave ?? 'sem-origem'}>
                <p title={grupo.chave ?? undefined} className={cn('line-clamp-2 break-words text-[13px] leading-snug', nome ? 'font-medium text-foreground' : 'italic text-muted-foreground')}>{nome ?? configuracao.semOrigem}</p>
                <div aria-hidden="true" className="relative mt-1.5 h-2.5 rounded-full bg-secondary">
                  {p90 !== null && <div className="absolute inset-y-0 left-0 rounded-full bg-[#d8ebaa] dark:bg-accent" style={{ width: `${Math.min(100, (p90 / escalaOrigens) * 100)}%` }} />}
                  {p50 !== null && <div className="absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-card bg-[#3f6212] dark:bg-[#bada55]" style={{ left: `${Math.min(100, (p50 / escalaOrigens) * 100)}%` }} />}
                </div>
                <p className="mt-1 text-xs tabular-nums text-muted-foreground">
                  mediana {p50 === null ? '—' : formatarDias(p50)}{p90 !== null ? ` · 9 em 10 até ${formatarDias(p90)}` : ''} · {quantidade} {quantidade === 1 ? 'venda' : 'vendas'}
                </p>
              </li>;
            })}
          </ol>}
      {grupos.ocultos > 0 && !carregandoOrigens && <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">{grupos.ocultos} {grupos.ocultos === 1 ? 'origem com menos' : 'origens com menos'} de {MINIMO_VENDAS_MEDIANA} vendas não {grupos.ocultos === 1 ? 'aparece' : 'aparecem'}.</p>}
    </div>
  </div>;
}

// Rótulos P50/P75/P90 muito próximos (ex.: 7 e 8 dias) se sobreporiam: o seguinte desce uma linha.
function posicionarMarcadores(itens: Array<{ chave: string; rotulo: string; dias: number }>) {
  const DISTANCIA_MINIMA = 0.09;
  const ultimaPosicaoPorLinha = [-1, -1];
  return itens.map((item) => {
    const posicao = posicaoNasFaixas(item.dias);
    const linha = posicao - ultimaPosicaoPorLinha[0] >= DISTANCIA_MINIMA ? 0 : 1;
    ultimaPosicaoPorLinha[linha] = posicao;
    return { ...item, posicao, linha };
  });
}
